
import PageContent from "~/components/news/inner/PageContent";
import { apiContentError } from "~/utils/api-content";

const technicalErrorId = "62466021-2d2c-4be8-9fdf-6ae36ad7cca3";

function trackPageLoadError(error) {
    if (typeof window === "undefined") return;
    if (!process.client) return;

    const trackingDetails = {
        error_id: technicalErrorId,
        timestamp: new Date().toISOString(),
        request_status: error?.response?.status || "unknown",
        request_path: error?.config?.url || "unknown",
        error_message: error?.message || "unknown",
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        ...trackingDetails,
        event: "technical_error",
    });

    window.InsiderQueue = window.InsiderQueue || [];
    window.InsiderQueue.length = 0;

    window.InsiderQueue.push({
        type: "custom_event",
        value: [
            {
                event_name: "technical_error",
                event_parameters: {
                    custom: trackingDetails,
                },
            },
        ],
    });

    window.InsiderQueue.push({
        type: "init",
    });
}

async function getPageContent($axios, url) {
    const relativeUrl = url.replace(/^\//, "");
    const response = await $axios.get(relativeUrl, { timeout: 60000 });

    const payload = response?.data?.res;
    const news = payload?.news;

    if (!news || typeof news !== "object" || Array.isArray(news) || !news.title) {
        throw apiContentError("Article response is missing its news content.", response);
    }

    return {
        news,
        relatedNews: Array.isArray(payload?.related_news) ? payload.related_news : [],
    };
}

export default {
    spacingDebug: false,
    components: {
        PageContent,
    },

    async asyncData({ $axios, params, redirect, error, store }) {
        if (process.client) {
            store.commit("global/loader/toggleLoaderStatus", {
                status: true,
            });
        }

        try {
            const { news, relatedNews } = await getPageContent($axios, `web/page/news/info/${encodeURIComponent(params.news_article)}`);

            if (news.slug_redirect_from === params.news_article && news.slug && news.slug !== params.news_article) {
                return redirect(301, `/news/${encodeURIComponent(news.slug)}`);
            }

            return {
                news_info: news,
                related_news: relatedNews,
            };
        } catch (fetchError) {
            console.error("Unable to load news article:", {
                code: fetchError.code || "UNKNOWN",
                status: fetchError.response?.status || "no response",
            });

            // Only track analytics on client side
            if (process.client) {
                trackPageLoadError(fetchError);
            }

            const statusCode = fetchError.response?.status === 404 ? 404 : 502;

            return error({
                statusCode,
                message: statusCode === 404 ? "News article not found." : "Unable to load the news article.",
            });
        } finally {
            if (process.client) {
                store.commit("global/loader/toggleLoaderStatus", {
                    status: false,
                });
            }
        }
    },

    mounted() {
        this.scrollToTop();
    },

    head() {
        const news = this.news_info || {};

        /*
         * Remove trailing slash while preserving root "/".
         */
        const canonicalPath = this.$route.path.replace(/\/+$/, "") || "/";

        const canonicalUrl = `https://malayan.com${canonicalPath}`;

        const defaultDescription = "Malayan Insurance is the largest non-life insurer in the country. Protect yourself and your loved ones with our products today.";

        const title = `${news.meta_title || news.title || "News"} | Malayan Insurance`;

        const description = news.meta_description || defaultDescription;

        /*
         * Use the article image when available.
         * Fall back to the default Malayan image.
         */
        let imageUrl = "https://malayan.com/mico2022-[light].webp";

        try {
            const resolvedImageUrl = new URL(news.images?.[0]?.path || imageUrl, "https://malayan.com");

            if (["https:", "http:"].includes(resolvedImageUrl.protocol)) {
                imageUrl = resolvedImageUrl.href;
            }
        } catch (_) {
            // Keep the default image.
        }

        const imageAlt = news.images?.[0]?.alt || news.title || "Malayan Insurance News";

        return {
            title,

            link: [
                {
                    hid: "canonical",
                    rel: "canonical",
                    href: canonicalUrl,
                },
                {
                    hid: "alternate-en",
                    rel: "alternate",
                    hreflang: "en",
                    href: canonicalUrl,
                },
            ],

            meta: [
                // Article metadata
                {
                    hid: "description",
                    name: "description",
                    content: description,
                },
                {
                    hid: "keywords",
                    name: "keywords",
                    content: news.keywords || "Malayan Insurance news",
                },
                {
                    hid: "author",
                    name: "author",
                    content: news.author || "Malayan Insurance",
                },
                {
                    hid: "robots",
                    name: "robots",
                    content: "index, follow",
                },

                // Twitter
                {
                    hid: "twitter:card",
                    name: "twitter:card",
                    content: "summary_large_image",
                },
                {
                    hid: "twitter:title",
                    name: "twitter:title",
                    content: title,
                },
                {
                    hid: "twitter:description",
                    name: "twitter:description",
                    content: description,
                },
                {
                    hid: "twitter:image",
                    name: "twitter:image",
                    content: imageUrl,
                },

                // Open Graph
                {
                    hid: "og:title",
                    property: "og:title",
                    content: title,
                },
                {
                    hid: "og:description",
                    property: "og:description",
                    content: description,
                },
                {
                    hid: "og:url",
                    property: "og:url",
                    content: canonicalUrl,
                },
                {
                    hid: "og:image",
                    property: "og:image",
                    content: imageUrl,
                },
                {
                    hid: "og:image:alt",
                    property: "og:image:alt",
                    content: imageAlt,
                },
                {
                    hid: "og:site_name",
                    property: "og:site_name",
                    content: "Malayan Insurance (Philippines)",
                },
                {
                    hid: "og:type",
                    property: "og:type",
                    content: "article",
                },
            ],
        };
    },
};
